import { vetor, type Vetor3 } from '../geometria/vetor3'
import type { AncoraOcupante, PerfilVeiculo } from './perfilVeiculo'

/**
 * Medidas dos bancos.
 *
 * Vivem no domínio porque duas coisas dependem delas: o desenho do habitáculo e
 * o cálculo de obstáculos da rota de retirada. Se cada lado tivesse a sua
 * cópia, o banco desenhado e o banco que atrapalha deixariam de ser o mesmo
 * banco — e só a tela mostraria, mal.
 */
export interface MedidasBanco {
  assento: { largura: number; altura: number; profundidade: number; offsetZ: number }
  encosto: {
    largura: number
    altura: number
    espessura: number
    offsetY: number
    offsetZ: number
    inclinacaoRad: number
  }
  apoioCabeca: {
    largura: number
    altura: number
    espessura: number
    offsetY: number
    offsetZ: number
  }
}

export const BANCO_DIANTEIRO: MedidasBanco = {
  assento: { largura: 0.46, altura: 0.1, profundidade: 0.48, offsetZ: 0.06 },
  encosto: {
    largura: 0.44,
    altura: 0.62,
    espessura: 0.11,
    offsetY: 0.3,
    offsetZ: -0.21,
    inclinacaoRad: -0.18,
  },
  apoioCabeca: { largura: 0.24, altura: 0.16, espessura: 0.1, offsetY: 0.68, offsetZ: -0.16 },
}

export const BANCO_TRASEIRO: MedidasBanco = {
  assento: { largura: 0.46, altura: 0.1, profundidade: 0.46, offsetZ: 0.06 },
  encosto: {
    largura: 0.44,
    altura: 0.56,
    espessura: 0.11,
    offsetY: 0.27,
    offsetZ: -0.2,
    inclinacaoRad: -0.126,
  },
  apoioCabeca: { largura: 0.22, altura: 0.14, espessura: 0.1, offsetY: 0.6, offsetZ: -0.15 },
}

export const medidasDaAncora = (ancora: AncoraOcupante): MedidasBanco =>
  ancora.fila === 'dianteira' ? BANCO_DIANTEIRO : BANCO_TRASEIRO

/** Quanto o encosto tomba para trás quando o banco é reclinado. */
export const RECLINACAO_ENCOSTO_RAD = 0.85
/**
 * Quanto o encosto do banco TRASEIRO tomba para a frente quando é rebatido.
 *
 * O banco de trás não reclina: ele deita sobre o assento, para a frente. O
 * valor leva o encosto, já inclinado de fábrica, a ficar quase deitado.
 */
export const REBATIMENTO_ENCOSTO_TRASEIRO_RAD = 1.45

/** O encosto desta posição deita para a frente (fila de trás) em vez de reclinar para trás. */
export const encostoRebateParaFrente = (ancora: AncoraOcupante): boolean => ancora.fila === 'traseira'

/** Quanto o assento recua no trilho. */
export const RECUO_TRILHO = 0.22

/** Ids das peças do banco de uma posição de ocupante. */
export function idsDoBanco(ancora: AncoraOcupante): {
  conjunto: string
  assento: string
  encosto: string
  apoioCabeca: string
} {
  const sufixo = ancora.id.charAt(0).toUpperCase() + ancora.id.slice(1)
  const conjunto = `banco${sufixo}`
  return {
    conjunto,
    assento: `${conjunto}.assento`,
    encosto: `${conjunto}.encosto`,
    apoioCabeca: `${conjunto}.apoioCabeca`,
  }
}

/**
 * Centro de cada peça do banco, em coordenadas de mundo.
 *
 * Encosto e apoio de cabeça giram em torno da dobradiça na base do encosto — a
 * mesma que a cena usa. Reclinar não só baixa o topo: ele RECUA, e é o recuo
 * que abre espaço na rota de retirada.
 */
export function centrosDoBanco(
  ancora: AncoraOcupante,
  reclinado = false,
): { assento: Vetor3; encosto: Vetor3; apoioCabeca: Vetor3 } {
  const m = medidasDaAncora(ancora)
  const { x, z } = ancora.posicao
  const base = ancora.alturaAssento
  const zDobradica = z + m.encosto.offsetZ

  // ângulo total: a inclinação de fábrica mais a reclinação (para trás) ou o
  // rebatimento (para a frente, no banco de trás), se houver
  const giro = encostoRebateParaFrente(ancora) ? -REBATIMENTO_ENCOSTO_TRASEIRO_RAD : RECLINACAO_ENCOSTO_RAD
  const angulo = -m.encosto.inclinacaoRad + (reclinado ? giro : 0)
  const cos = Math.cos(angulo)
  const sen = Math.sin(angulo)

  /** gira um deslocamento (dy, dz) em torno da dobradiça; ângulo positivo tomba para trás */
  const girar = (dy: number, dz: number): Vetor3 =>
    vetor(x, base + dy * cos + dz * sen, zDobradica - dy * sen + dz * cos)

  return {
    assento: vetor(x, base, z + m.assento.offsetZ),
    encosto: girar(m.encosto.offsetY, 0),
    apoioCabeca: girar(m.apoioCabeca.offsetY, m.apoioCabeca.offsetZ - m.encosto.offsetZ),
  }
}

/** A largura do banco central traseiro depende da largura útil do veículo. */
export function larguraDoBanco(perfil: PerfilVeiculo, ancora: AncoraOcupante): number {
  const m = medidasDaAncora(ancora)
  if (ancora.lado !== 'central') return m.assento.largura
  return Math.min(m.assento.largura, perfil.largura - 0.22 - 2 * m.assento.largura)
}
